import AppThemed from "@/AppThemed.tsx";
import {installErrorReporter} from "@/utilities/errorReporter.ts";
import {DefaultCatchBoundary} from "@/components/DefaultCatchBoundary.tsx";
import JsonLdOrganizationSchema from "@/components/JsonLdOrganizationSchema.tsx";
import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import {Capacitor} from "@capacitor/core";
import {createRootRoute, HeadContent, Link, Outlet, Scripts} from '@tanstack/react-router'
import {TanStackRouterDevtools} from '@tanstack/react-router-devtools'
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import React, {useEffect} from "react";
import AppContainer from "../AppContainer.tsx";
import {config} from "../config/config.ts";
import Public404Page from "../landing/Public404Page.tsx";

import swipeable_list_styles from 'react-swipeable-list/dist/styles.css?url';
import styles_init from './../style_init.css?url';
import styles from './../style.scss?url';

import {initializeApp} from "firebase/app";

dayjs.extend(advancedFormat);

// EEA + UK + Switzerland — regions where ad/marketing cookies need prior consent. Shared by the
// Consent Mode default (SSR <head>) and the Meta Pixel gate (TrackingScripts) so they never drift.
const CONSENT_REGIONS = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','IS','LI','NO','GB','CH'];
const CONSENT_REGIONS_JSON = JSON.stringify(CONSENT_REGIONS);

if (typeof window !== 'undefined' && config.bugsnagApiKey && !Bugsnag.isStarted()) {
    Bugsnag.start({
        apiKey:       config.bugsnagApiKey,
        releaseStage: config.bugsnagReleaseStage,
        appVersion:   config.version,
        appType:      Capacitor.getPlatform(),
        plugins:      [new BugsnagPluginReact()],
        onError:      function (event) {
            event.addMetadata('debug', '_errors', event.errors);

            if (event.device.userAgent?.toLowerCase().includes('phantomjs')) {
                return false;
            }
            if (document.location.host.includes('.translate.goog')) {
                return false;
            }
        },
    });
    BugsnagPerformance.start({apiKey: config.bugsnagApiKey});
}

// In-house friction log (Admin → Client Errors): JS errors, dead/rage clicks, failed API calls.
// Complements Bugsnag — runs on every page, public and app alike.
if (typeof window !== 'undefined') {
    installErrorReporter();
}

export const Route = createRootRoute({
    head: () => ({
        meta: [
            {
                charSet: 'utf-8',
            },
            {
                name: 'viewport',
                content: `initial-scale=1, height=device-height, width=device-width, viewport-fit=cover${config.isCordova ? ', maximum-scale=1.0, user-scalable=no' : ''}`,
            },
            { name: 'format-detection', content: 'telephone=no' },
            { name: 'msapplication-tap-highlight', content: 'no' },
            { name: 'color-scheme', content: 'light dark' },
            // Meta Business domain verification. Lives here rather than index.html because the
            // served <head> is rendered by this route — index.html is not the shell any more.
            { name: 'facebook-domain-verification', content: 'no1oxgcais1aecmanxvfskt736nude' },
        ],
        links: [
            {
                rel: 'apple-touch-icon',
                sizes: '180x180',
                href: '/favicon-180x180.png',
            },
            {
                rel: 'icon',
                type: 'image/png',
                href: '/favicon-180x180.png',
            },
            { rel: "stylesheet", href: styles_init },
            { rel: 'manifest', href: '/site.webmanifest', color: '#fffff' },
        ],
    }),
    errorComponent: (props) => {
        return (
            <RootLayout>
                <DefaultCatchBoundary {...props} />
            </RootLayout>
        )
    },
    notFoundComponent: () => <Public404Page />,
    component: RootComponent,
})

function RootComponent() {
    // noinspection SpellCheckingInspection

    useEffect(() => {
        if (Capacitor.getPlatform() === 'web') {
            return;
        }

        const enableSafeArea = async () => {
            const [{SafeArea}] = await Promise.all([
                import('@capacitor-community/safe-area'),
            ]);
            SafeArea.enable({
                config: {
                    customColorsForSystemBars: true,
                    statusBarColor:            '#25252580', // transparent
                    statusBarContent:          'dark',
                    navigationBarColor:        '#25252580', // transparent
                    navigationBarContent:      'dark',
                },
            });
        };

        if ((document as any).deviceready || Capacitor.isNativePlatform()) {
            enableSafeArea();
        } else {
            document.addEventListener('deviceready', enableSafeArea, {once: true});
        }

        return () => {
            document.removeEventListener('deviceready', enableSafeArea);
        };
    }, []);

    // Belt-and-suspenders: noindex any non-production host (staging/dev/preview),
    // regardless of which build it is serving. Production is scorepal.co / www only.
    useEffect(() => {
        if (typeof window === 'undefined' || Capacitor.isNativePlatform()) return;
        const host = window.location.hostname;
        const isProd = host === 'scorepal.co' || host === 'www.scorepal.co';
        const isLocal = host === 'localhost' || host === '127.0.0.1';
        if (isProd || isLocal) return;
        if (document.querySelector('meta[data-noindex-nonprod]')) return;
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        meta.setAttribute('data-noindex-nonprod', '');
        document.head.appendChild(meta);
    }, []);

    if (typeof window !== 'undefined') {
        initializeApp({
            apiKey:            "AIzaSyD3BwzqmvdMhK0jFVhnaCAC5zRGMhfoCXY",
            authDomain:        "scorepal-co.firebaseapp.com",
            projectId:         "scorepal-co",
            storageBucket:     "scorepal-co.firebasestorage.app",
            messagingSenderId: "807792745504",
            appId:             "1:807792745504:web:5fc95be76db60c47e03045",
            measurementId:     "G-H18SDMTRK9"
        });
    }
    
    return (
        <RootLayout>
            <Outlet />
        </RootLayout>
    )
}

function TrackingScripts() {
    useEffect(() => {
        if (window.location.hash === '#no-tracking') {
            return;
        }

        const head = document.head;

        const gtagScript = document.createElement('script');
        gtagScript.async = true;
        gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17042305006';
        head.appendChild(gtagScript);

        const gtagInit = document.createElement('script');
        gtagInit.innerHTML = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'AW-17042305006');`;
        head.appendChild(gtagInit);

        // Google Funding Choices — certified CMP that shows the GDPR consent banner (authored in
        // AdSense → Privacy & messaging) and calls gtag('consent','update',…) on the visitor's choice.
        const fundingChoices = document.createElement('script');
        fundingChoices.async = true;
        fundingChoices.src = 'https://fundingchoicesmessages.google.com/i/pub-6471700087527589?ers=1';
        head.appendChild(fundingChoices);

        const fundingChoicesPresent = document.createElement('script');
        fundingChoicesPresent.innerHTML = `(function(){function signalGooglefcPresent(){if(!window.frames['googlefcPresent']){if(document.body){const i=document.createElement('iframe');i.style='width:0;height:0;border:none;z-index:-1000;left:-1000px;top:-1000px;';i.style.display='none';i.name='googlefcPresent';document.body.appendChild(i);}else{setTimeout(signalGooglefcPresent,0);}}}signalGooglefcPresent();})();`;
        head.appendChild(fundingChoicesPresent);

        const ahrefsScript = document.createElement('script');
        ahrefsScript.async = true;
        ahrefsScript.src = 'https://analytics.ahrefs.com/analytics.js';
        ahrefsScript.dataset.key = 'fG3P7At+nhOmrUmoKxNt8A';
        head.appendChild(ahrefsScript);

        const chatlingConfig = document.createElement('script');
        chatlingConfig.innerHTML = `window.chtlConfig = { chatbotId: "9164872542" }`;
        head.appendChild(chatlingConfig);

        const chatlingScript = document.createElement('script');
        chatlingScript.async = true;
        chatlingScript.dataset.id = '9164872542';
        chatlingScript.id = 'chtl-script';
        chatlingScript.type = 'text/javascript';
        chatlingScript.src = 'https://chatling.ai/js/embed.js';
        head.appendChild(chatlingScript);

        // Meta Pixel — gated to the SAME Google consent banner (no separate form). Held via
        // consent:revoke (no cookies, events queue), then released when EITHER the visitor is outside
        // the consent regions (rest-of-world, via Cloudflare same-origin /cdn-cgi/trace geo) OR they
        // accept in the Google CMP (observed as a Consent Mode 'update' on the dataLayer). A visitor
        // in a consent region who declines is never granted.
        const metaPixel = document.createElement('script');
        metaPixel.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('consent','revoke');fbq('init','2202172970198501');fbq('track','PageView');(function(){var granted=false;function grant(){if(!granted){granted=true;fbq('consent','grant');}}var dl=window.dataLayer=window.dataLayer||[];function scan(a){if(a&&a[0]==='consent'&&a[1]==='update'){var p=a[2]||{};if(p.ad_user_data==='granted'||p.ad_storage==='granted')grant();}}for(var i=0;i<dl.length;i++)scan(dl[i]);var op=dl.push;dl.push=function(){for(var i=0;i<arguments.length;i++)scan(arguments[i]);return op.apply(this,arguments);};var regions=${CONSENT_REGIONS_JSON};fetch('/cdn-cgi/trace').then(function(r){return r.text();}).then(function(t){var m=t.match(/loc=([A-Z]{2})/);var loc=m?m[1]:'';if(loc&&regions.indexOf(loc)===-1)grant();}).catch(function(){});})();`;
        document.body.insertBefore(metaPixel, document.body.firstChild);

        if (config.clarityProjectId && Capacitor.getPlatform() !== 'ios') {
            import("@microsoft/clarity").then((Clarity) => Clarity.default.init(config.clarityProjectId)).catch(() => {});
        }
    }, []);

    return null;
}

function RootLayout({ children }: { children: React.ReactNode }) {
    return (
        <html lang="en" className="light">
        <head>
            <HeadContent />
            {/* Consent Mode v2 — deny ad/analytics storage for EEA/UK/CH until the Google-certified
                CMP (Funding Choices, loaded in TrackingScripts) records the visitor's choice. This is
                server-rendered so it runs at HTML parse, before any tag or ad script loads. No region
                entry for rest-of-world ⇒ granted by default (no banner), matching Google's geo-gating. */}
            <script
                dangerouslySetInnerHTML={{
                    __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',wait_for_update:500,region:${CONSENT_REGIONS_JSON}});`,
                }}
            />
            {/* Keep non-production builds (staging/dev/local) out of the search index. */}
            {config.publicUrl !== 'https://scorepal.co' && (
                <meta name="robots" content="noindex, nofollow" />
            )}
            {/* Google Fonts */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        </head>
        <body>
        <TrackingScripts />
        <JsonLdOrganizationSchema routeId={Route.id} />
        <AppContainer>
            <AppThemed>
                {children}
            </AppThemed>
        </AppContainer>
        <TanStackRouterDevtools position="bottom-right" />
        <Scripts />

        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&display=swap" />
        <link rel="stylesheet" href="https://fonts.cdnfonts.com/css/satoshi" />
        <link rel="stylesheet" href={styles} />
        <link rel="stylesheet" href={swipeable_list_styles} />
        </body>
        </html>
    )
}
/*

export const Route = createRootRoute({
    component: () => {
        const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React)

        return <>
            {ErrorBoundary
                ? <ErrorBoundary FallbackComponent={(e) =>
                    (config.isTestMode
                        ? <div className="my-4 mt-8 w-3/4 mx-auto"><h3 className="mb-2">An error has occurred:</h3>
                            <pre className="text-xs">
                                        {e.error?.message}<br />
                                {e?.error.stack}
                                    </pre>
                        </div>
                        : <div className="my-4 mt-8 w-3/4 mx-auto"><h3 className="mb-2">
                            An error has occurred. We have been notified and will look into it.</h3>
                            <a href="/" className="underline">Go to the front page</a>
                        </div>)
                }>
                    <AppContainer />
                </ErrorBoundary>
                : <AppContainer />
            }
            <TanStackRouterDevtools position={"bottom-right"} />
        </>
    },

    notFoundComponent: () => (
        <Public404Page />
    ),
})
*/
